import { DEEP_DIVES } from "./deepDiveSamples";

/**
 * Deep Dive: a structured, visual walk through how a story flows. Instead of more paragraphs, a
 * story is decomposed into typed narrative BEATS (a scrollable thread), a small causal MAP
 * (connect the dots), a strip of grounded KEY FACTS, and anticipated QUESTIONS. Every field is
 * grounded in the card's own reported content (no invented causality), so the deep dive can only
 * clarify the story, never embellish it.
 *
 * For this first release the content is pre-authored for a sample set of cards and bundled with
 * the app (keyed by card id). The production path (a grounded, gate-checked pipeline pass writing
 * a `deep_dive` column served by the worker) is a follow-up; the UI here is the real thing.
 *
 * House style: no em dashes or en dashes anywhere in copy.
 */

/** The fixed narrative phases, so the UI can assign a label + icon deterministically. */
export type BeatPhase = "background" | "trigger" | "event" | "impact" | "why" | "next";

/** A node's role in the causal chain, so the map can colour cause vs turning-point vs outcome. */
export type NodeKind = "cause" | "pivot" | "effect" | "outcome";

/** A small, fixed icon vocabulary for key facts (kept tiny so it stays on-brand). */
export type FactIcon =
  | "number" | "place" | "time" | "money" | "people" | "scale" | "spark" | "flag" | "trend";

export interface DeepBeat {
  phase: BeatPhase;
  heading: string;
  line: string;
}
export interface KeyFact {
  label: string;
  value: string;
  /** which glyph to show; defaults to a neutral marker when absent. */
  icon?: FactIcon;
  /** one short line of extra context under the fact (what the number actually means). */
  hint?: string;
}
export interface MapNode {
  id: string;
  label: string;
  /** role in the chain (drives colour + a tiny kind tag). */
  kind?: NodeKind;
  /** an optional second line that adds detail to the step. */
  caption?: string;
}
export interface MapEdge {
  from: string;
  to: string;
  /** the verb on the connector ("knocks out", "so", "triggers") when the link is unambiguous. */
  label?: string;
}
export interface DeepQA {
  q: string;
  /** optional pre-written answer; the UI now launches the real Ask AI, so this is only a hint. */
  a?: string;
}
/**
 * The middle-tier "fuller story" read shown under the Deeper toggle: more than the card's short
 * body, less than the whole deep dive. A couple of grounded paragraphs plus, when the story truly
 * contains one, a verbatim direct quote. Authored + gate-checked by the deep-dive pipeline pass and
 * carried inside the same deepDive object, so it costs no extra field on the wire.
 */
export interface DeeperRead {
  paragraphs: string[];
  quote?: { text: string; speaker?: string };
}
export interface DeepDive {
  /** rough time to read the whole flow, in seconds. */
  seconds: number;
  /** one-line throughline: the whole story in a sentence (header + share artwork). */
  takeaway?: string;
  beats: DeepBeat[];
  keyFacts: KeyFact[];
  /** a small causal graph: how the beats connect (one cause can fan out to several effects). */
  map: { nodes: MapNode[]; edges: MapEdge[] };
  /** suggested questions that launch the real Ask AI chat (grounded in the card's sources). */
  questions: DeepQA[];
  /** the middle-tier read for the Deeper toggle (optional; absent on older cards). */
  deeperRead?: DeeperRead;
}

/** Human label for each beat phase (the eyebrow shown on a thread node). */
export const PHASE_LABEL: Record<BeatPhase, string> = {
  background: "Background",
  trigger: "The trigger",
  event: "What happened",
  impact: "The impact",
  why: "Why it matters",
  next: "What's next",
};

/** The deep dive for a card, if one has been authored. */
export function getDeepDive(cardId: string | undefined | null): DeepDive | null {
  if (!cardId) return null;
  return DEEP_DIVES[cardId] ?? null;
}

/** Whether a card has a deep dive to show (drives the entry chip). */
export function hasDeepDive(cardId: string | undefined | null): boolean {
  return !!cardId && cardId in DEEP_DIVES;
}

/** Minimal card shape needed to resolve a deep dive (avoids importing InSnipCard here).
 *  `hasDeepDive` is the marker a lean feed card carries in place of the dive body: the dive
 *  exists server-side and is fetched when the reader opens it. */
type DeepDiveCard =
  | { id?: string | null; deepDive?: DeepDive | null; hasDeepDive?: boolean }
  | null
  | undefined;

/** The deep dive for a card: the pipeline-authored one it carries, else a bundled sample (the
 *  demo cards), else null. This is what the app should call in production. */
export function deepDiveFor(card: DeepDiveCard): DeepDive | null {
  if (!card) return null;
  if (card.deepDive && typeof card.deepDive === "object") return card.deepDive;
  return getDeepDive(card.id);
}

/** Whether a card has a deep dive to show (live, bundled, or announced by a lean feed card
 *  whose body has not been hydrated yet). This is what UI affordances gate on: the chip must
 *  appear before the body arrives, otherwise a lean round would look like it had no dives. */
export function hasDeepDiveFor(card: DeepDiveCard): boolean {
  if (card && card.hasDeepDive === true) return true;
  return deepDiveFor(card) !== null;
}

/** A dive the server has but this client has not downloaded yet. */
export function needsDiveHydration(card: DeepDiveCard): boolean {
  return Boolean(card && card.hasDeepDive === true && !card.deepDive);
}

/**
 * Lay a causal map out in tidy top-to-bottom layers (roots first, then their effects), so any
 * small DAG renders as a clean flow: a linear chain stacks one per row, a one-to-many cause fans
 * its effects across a row. Returns the node ids per layer.
 */
export function layerMap(map: { nodes: MapNode[]; edges: MapEdge[] }): string[][] {
  const indeg = new Map<string, number>();
  const outs = new Map<string, string[]>();
  for (const n of map.nodes) {
    indeg.set(n.id, 0);
    outs.set(n.id, []);
  }
  for (const e of map.edges) {
    if (!indeg.has(e.to) || !outs.has(e.from)) continue;
    indeg.set(e.to, (indeg.get(e.to) ?? 0) + 1);
    outs.get(e.from)!.push(e.to);
  }
  const layerOf = new Map<string, number>();
  // roots (no incoming edge) start at layer 0
  let frontier = map.nodes.filter((n) => (indeg.get(n.id) ?? 0) === 0).map((n) => n.id);
  for (const id of frontier) layerOf.set(id, 0);
  let depth = 0;
  const guard = map.nodes.length + 2;
  while (frontier.length && depth < guard) {
    const next: string[] = [];
    for (const id of frontier)
      for (const t of outs.get(id) ?? []) {
        const cand = (layerOf.get(id) ?? 0) + 1;
        if (cand > (layerOf.get(t) ?? -1)) {
          layerOf.set(t, cand);
          next.push(t);
        }
      }
    frontier = next;
    depth += 1;
  }
  const layers: string[][] = [];
  for (const n of map.nodes) {
    const l = layerOf.get(n.id) ?? 0;
    (layers[l] ??= []).push(n.id);
  }
  return layers.filter(Boolean);
}

/**
 * Resolve the causal map into ready-to-render rows of full nodes plus, for each gap between
 * rows, the connector's verb when it is unambiguous. A verb is only shown when exactly one edge
 * crosses that boundary (a clean linear step); a fan-out shows a plain connector so the branching
 * itself carries the meaning and no label can mislead.
 */
export function flowLayers(map: { nodes: MapNode[]; edges: MapEdge[] }): {
  layers: MapNode[][];
  linkLabels: (string | null)[];
} {
  const ids = layerMap(map);
  const byId = new Map(map.nodes.map((n) => [n.id, n]));
  const layers = ids.map((row) =>
    row.map((id) => byId.get(id)).filter((n): n is MapNode => Boolean(n)),
  );
  const linkLabels: (string | null)[] = [];
  for (let i = 0; i < ids.length - 1; i++) {
    const a = new Set(ids[i]);
    const b = new Set(ids[i + 1]);
    const crossing = map.edges.filter((e) => a.has(e.from) && b.has(e.to));
    linkLabels.push(crossing.length === 1 ? (crossing[0].label ?? null) : null);
  }
  return { layers, linkLabels };
}

/** Suggested Ask prompts for a deep dive: the authored questions, capped and de-duplicated. */
export function askPrompts(deepDive: DeepDive, max = 4): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const qa of deepDive.questions) {
    const q = (qa.q || "").trim();
    const key = q.toLowerCase();
    if (q && !seen.has(key)) {
      seen.add(key);
      out.push(q);
    }
    if (out.length >= max) break;
  }
  return out;
}
